import { SCROLL_AXES, type ScrollAxis } from "./scroll-box-axes"
import type { UnusedRoomFinding } from "./scroll-box-findings"
import { identifyScrollBox } from "./scroll-box-identity"
import { rectSpan, windowSpan } from "./scroll-box-spans"
import { OVERFLOW_THRESHOLD_PX, clipsOrScrollsAlong, isOutOfFlow, isRendered, overflowsAlong } from "./scroll-box-styles"
import type { ScrollBoxVerdict } from "./scroll-box-verdict"

const ALIGNMENTS_AWAY_FROM_START: ReadonlySet<string> = new Set([
  "center",
  "safe center",
  "unsafe center",
  "end",
  "flex-end",
  "space-between",
  "space-around",
  "space-evenly",
])

interface UnusedRoom {
  frame: Element
  unusedPx: number
}

function placesContentAwayFromStart(container: Element, branch: Element, axis: ScrollAxis): boolean {
  const style = getComputedStyle(container)
  const branchStyle = getComputedStyle(branch)
  if (style.display.includes("flex")) {
    const mainAxisIsVertical = style.flexDirection.startsWith("column")
    if ((axis.name === "vertical") === mainAxisIsVertical) {
      return style.flexDirection.endsWith("reverse") || ALIGNMENTS_AWAY_FROM_START.has(style.justifyContent)
    }
    const crossAlignment = branchStyle.alignSelf === "auto" ? style.alignItems : branchStyle.alignSelf
    return ALIGNMENTS_AWAY_FROM_START.has(crossAlignment)
  }
  if (style.display.includes("grid")) {
    const trackAlignment = axis.name === "vertical" ? style.alignContent : style.justifyContent
    const cellAlignment = axis.name === "vertical" ? branchStyle.alignSelf : branchStyle.justifySelf
    return ALIGNMENTS_AWAY_FROM_START.has(trackAlignment) || ALIGNMENTS_AWAY_FROM_START.has(cellAlignment)
  }
  return false
}

function contentBoxEndPx(container: Element, axis: ScrollAxis): number {
  const startPx = container.getBoundingClientRect()[axis.rectStart] + container[axis.clientOffset]
  const paddingEndPx = parseFloat(getComputedStyle(container)[axis.paddingEnd])
  return Math.min(startPx + container[axis.clientSize] - paddingEndPx, windowSpan(axis).endPx)
}

function inFlowContentEndPx(container: Element, axis: ScrollAxis): number {
  const marginEnd = axis.name === "vertical" ? "marginBottom" : "marginRight"
  const ends = Array.from(container.children)
    .filter((child) => isRendered(child) && !isOutOfFlow(child))
    .map((child) => rectSpan(child.getBoundingClientRect(), axis).endPx + parseFloat(getComputedStyle(child)[marginEnd]))
  return Math.max(Number.NEGATIVE_INFINITY, ...ends)
}

function roomInside(container: Element, axis: ScrollAxis): number {
  return Math.max(0, contentBoxEndPx(container, axis) - inFlowContentEndPx(container, axis))
}

function unusedRoomAround(box: Element, axis: ScrollAxis): UnusedRoom | null {
  let branch = box
  for (let container = box.parentElement; container && container !== document.documentElement; container = container.parentElement) {
    if (placesContentAwayFromStart(container, branch, axis)) return null
    if (clipsOrScrollsAlong(container, axis)) {
      return overflowsAlong(container, axis) ? null : { frame: container, unusedPx: roomInside(container, axis) }
    }
    const unusedPx = roomInside(container, axis)
    if (isOutOfFlow(container) || unusedPx > OVERFLOW_THRESHOLD_PX) return { frame: container, unusedPx }
    branch = container
  }
  return null
}

export function detectUnusedRoom(box: Element, verdict: ScrollBoxVerdict): UnusedRoomFinding[] {
  if (isOutOfFlow(box)) return []
  return verdict.overflowingAxes.flatMap((axisVerdict) => {
    const axis = SCROLL_AXES.find((candidate) => candidate.name === axisVerdict.axis)
    const room = axis ? unusedRoomAround(box, axis) : null
    if (!room || room.unusedPx < axisVerdict.medianUnitPx) return []
    const finding: UnusedRoomFinding = {
      kind: "unused-room",
      identity: identifyScrollBox(box),
      boxWidthPx: box.clientWidth,
      boxHeightPx: box.clientHeight,
      axis: axisVerdict.axis,
      frameKey: identifyScrollBox(room.frame).key,
      unusedRoomPx: room.unusedPx,
      medianUnitPx: axisVerdict.medianUnitPx,
    }
    return [finding]
  })
}
